import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Badge, Button, Col, Collapse, Dropdown, Form, Menu, Modal, notification, Row, Space, Spin, Tabs, Tooltip } from 'antd';
import { DownloadOutlined, DownOutlined, ExportOutlined, EyeOutlined, LoadingOutlined, ReloadOutlined, SaveOutlined, UserOutlined } from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, ColDef } from 'ag-grid-community';
import { IntlShape, useIntl } from 'react-intl';
import { IAppComponentProps, PortalUser } from '../../components';
import client from '../../config/GraphQLApolloClient';
import { QUERY_SELECT_OPTIONS, QUERY_PARTNER_DETAILS } from './obtQueries';
import { IObtSurveySearchCriteriaInput, ObtPartnerDetailResponse } from '../../__generated__/types';
import { SelectOption } from './interfaces';
import ObtPartnerListDatasource from './ObtPartnerListDatasource';
import ObtSearchForm from './components/ObtSearchForm';
import ObtPartnerContactsBulkUpdateTab from './components/ObtUpdateTab';
import ObtPartnerDetailsTab, { PartnerIdentity } from './components/ObtPartnerDetailsTab';
import { FileSearchRow } from './components/ObtSearchByFileModal';
import styles from './ObtLandingPage.module.scss';
import { Obt_DownloadSurveyPartnerDataDocument } from './obtDownloadQueries.generated';
import { PARTNER_DOWNLOAD_SURVEY_EXCEL } from './obtDownloadQueries';
import { exportMonitoringData } from '../eim/excelUtil';
import dayjs from 'dayjs';
import { PersonalizeGridView } from '../info-portal/components/grid/GridUtil';
import { convertFromSpecifiedDateFormatToUserDefined } from '../users/UserFormatPreferenceServices';
import { saveAs } from 'file-saver';
import { getObtAccessRights } from './obtEntitlements';

// ─── Column definitions ───────────────────────────────────────────────────────


/**
 * Formats a "YYYY-MM-DD HH:mm:ss" value using the user's preferred date format,
 * preserving the time portion (HH:mm:ss).
 */
const formatDateTimeForUser = (user: PortalUser, value: string | null | undefined): string => {
    if (!value) return '';
    const datePart = value.substring(0, 10);
    const timePart = value.substring(10); // includes leading space + "HH:mm:ss"
    const formattedDate = convertFromSpecifiedDateFormatToUserDefined(user, datePart, 'YYYY-MM-DD');
    return formattedDate ? `${formattedDate}${timePart}` : '';
};

type SetFilterValues = {
    systemCodes: string[];
    categories: string[];
    companyCodes: string[];
    countryCodes: string[];
    surveyStatuses: string[];
    surveyTypes: string[];
};

const STATIC_PARTNER_CATEGORY_VALUES = ['C', 'V'];
const STATIC_SURVEY_STATUS_VALUES = ['NOT STARTED', 'IN PROGRESS', 'COMPLETED', 'CANCELLED', 'NO SURVEY'];
const STATIC_SURVEY_TYPE_VALUES = ['EINV'];

const mapOptionValues = (options: SelectOption[]): string[] =>
    Array.from(
        new Set(
            options
                .map((option) => option.value?.trim())
                .filter((value): value is string => !!value)
        )
    );

const buildSetFilterValues = (
    systemOptions: SelectOption[],
    companyCodeOptions: SelectOption[],
    countryOptions: SelectOption[],
): SetFilterValues => ({
    systemCodes: mapOptionValues(systemOptions),
    categories: STATIC_PARTNER_CATEGORY_VALUES,
    companyCodes: mapOptionValues(companyCodeOptions),
    countryCodes: mapOptionValues(countryOptions),
    surveyStatuses: STATIC_SURVEY_STATUS_VALUES,
    surveyTypes: STATIC_SURVEY_TYPE_VALUES,
});

const buildPartnerColumns = (_intl: IntlShape, currentUser: PortalUser, setFilterValues: SetFilterValues): ColDef[] => [
    {
        field: 'partnerNo',
        colId: 'partnerNo',
        headerName: 'Partner No',
        width: 130,
        filter: 'agTextColumnFilter',
    },
    {
        field: 'systemCode',
        colId: 'systemCode',
        headerName: 'System',
        width: 100,
        filter: 'agSetColumnFilter',
        filterParams: { values: setFilterValues.systemCodes },
    },
    {
        field: 'masterPartnerNo',
        colId: 'masterPartnerNo',
        headerName: 'Master Partner No',
        width: 155,
        filter: 'agTextColumnFilter',
    },
    {
        field: 'partnerName',
        colId: 'partnerName',
        headerName: 'Partner Name',
        flex: 1,
        minWidth: 160,
        filter: 'agTextColumnFilter',
    },
    {
        field: 'category',
        colId: 'category',
        headerName: 'Category',
        width: 100,
        filter: 'agSetColumnFilter',
        filterParams: { values: setFilterValues.categories },
    },
    {
        field: 'companyCode',
        colId: 'companyCode',
        headerName: 'Company Code',
        width: 135,
        filter: 'agSetColumnFilter',
        filterParams: { values: setFilterValues.companyCodes },
    },
    {
        field: 'insertTime',
        colId: 'insertTime',
        headerName: 'Master Data Insert Date',
        width: 200,
        valueGetter: (params) => formatDateTimeForUser(currentUser, params.data?.insertTime),
        filter: 'agDateColumnFilter',
    },
    {
        field: 'countryCode',
        colId: 'countryCode',
        headerName: 'Country',
        width: 100,
        filter: 'agSetColumnFilter',
        filterParams: { values: setFilterValues.countryCodes },
    },
    {
        field: 'apArPartnerLink',
        colId: 'apArPartnerLink',
        headerName: 'AP AR Partner Link',
        width: 155,
        filter: 'agTextColumnFilter',
    },
    {
        field: 'surveyStatus',
        colId: 'surveyStatus',
        headerName: 'Survey Status',
        width: 155,
        filter: 'agSetColumnFilter',
        filterParams: { values: setFilterValues.surveyStatuses },
    },
    {
        field: 'surveyStartDate',
        colId: 'surveyStartDate',
        headerName: 'Survey Creation Date',
        width: 200,
        valueGetter: (params) => formatDateTimeForUser(currentUser, params.data?.surveyStartDate),
    },
    {
        field: 'surveyId',
        colId: 'surveyId',
        headerName: 'Survey ID',
        width: 110,
        filter: 'agNumberColumnFilter',
    },
    {
        field: 'surveyType',
        colId: 'surveyType',
        headerName: 'Survey Type',
        width: 130,
        filter: 'agSetColumnFilter',
        filterParams: { values: setFilterValues.surveyTypes },
    },
    {
        field: 'remark',
        colId: 'remark',
        headerName: 'Comment',
        width: 140,
        filter: 'agTextColumnFilter',
    },
];

const DEFAULT_COL_DEF: ColDef = {
    resizable: true,
    sortable: true,
    filter: false,
};

const CACHE_BLOCK_SIZE = 200;
const ALL_RESULTS_PAGE_SIZE = 100_000;

// ─── Component ────────────────────────────────────────────────────────────────

const ObtLandingPage: FunctionComponent<IAppComponentProps> = (props) => {
    const intl = useIntl();
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [countryOptions, setCountryOptions] = useState<SelectOption[]>([]);
    const [companyCodeOptions, setCompanyCodeOptions] = useState<SelectOption[]>([]);
    const [systemOptions, setSystemOptions] = useState<SelectOption[]>([]);
    const [searchFilters, setSearchFilters] = useState<IObtSurveySearchCriteriaInput>({
        pageNo: 0,
        pageSize: CACHE_BLOCK_SIZE,
    });
    const [_lastSearch, setLastSearch] = useState<IObtSurveySearchCriteriaInput | null>(null);
    const lastSearchRef = useRef<IObtSurveySearchCriteriaInput | null>(null);
    const [, forceMenuRefresh] = useReducer((v: number) => v + 1, 0);
    const [activeTab, setActiveTab] = useState<string>('search');
    const obtAccessRights = useMemo(
        () => getObtAccessRights(props.distinctEntitlements ?? []),
        [props.distinctEntitlements]
    );

    // ─── Partner details state ────────────────────────────────────────────────
    const [partnerIdentity, setPartnerIdentity] = useState<PartnerIdentity | null>(null);
    const [partnerDetails, setPartnerDetails] = useState<ObtPartnerDetailResponse | null>(null);

    const trackLastSearch = useCallback((criteria: IObtSurveySearchCriteriaInput) => {
        lastSearchRef.current = criteria;
        setLastSearch(criteria);
    }, []);

    const gridApi = useRef<GridApi | null>(null);
    const columnDefs = useRef<ColDef[]>(buildPartnerColumns(
        props.intl,
        props.currentUser,
        buildSetFilterValues([], [], [])
    ));
    const gridView = useRef<PersonalizeGridView>(
        new PersonalizeGridView('obt_partner_list_ag_grid_personalized_view', columnDefs.current)
    );

    useEffect(() => {
        const updatedColumns = buildPartnerColumns(
            props.intl,
            props.currentUser,
            buildSetFilterValues(systemOptions, companyCodeOptions, countryOptions)
        );

        columnDefs.current = updatedColumns;
        gridView.current = new PersonalizeGridView('obt_partner_list_ag_grid_personalized_view', updatedColumns);

        if (gridApi.current) {
            gridView.current.initPersonalizedView(gridApi.current);
            gridApi.current.setColumnDefs(gridView.current.getView());
        }
    }, [companyCodeOptions, countryOptions, props.currentUser, props.intl, systemOptions]);

    // ─── Load dropdown options on mount ──────────────────────────────────────
    useEffect(() => {
        if (!obtAccessRights.canAccessObt) {
            return;
        }

        setOptionsLoading(true);
        client
            .query({
                query: QUERY_SELECT_OPTIONS,
                variables: { lang: localStorage.getItem('locale') || 'en' },
                fetchPolicy: 'network-only',
            })
            .then((response: any) => {
                const opts = response?.data?.obtGetSearchSelectionOptions;
                setCountryOptions(opts?.countries ?? []);
                setCompanyCodeOptions(opts?.companyCodes ?? []);
                setSystemOptions(opts?.systemCodes ?? []);
            })
            .catch((err: any) => console.error('Failed to load OBT select options', err))
            .finally(() => setOptionsLoading(false));
    }, [obtAccessRights.canAccessObt]);

    useEffect(() => {
        if (!obtAccessRights.canMaintainBulkUpdate && activeTab === 'update') {
            setActiveTab('search');
        }
    }, [activeTab, obtAccessRights.canMaintainBulkUpdate]);

    // ─── Grid events ─────────────────────────────────────────────────────────
    const onGridReady = useCallback(
        (params: any) => {
            gridApi.current = params.api;
            gridView.current.initPersonalizedView(params.api);
            params.api.setServerSideDatasource(
                new ObtPartnerListDatasource(client, searchFilters, CACHE_BLOCK_SIZE, trackLastSearch)
            );
        },
        [searchFilters, trackLastSearch]
    );

    const onFirstDataRendered = useCallback((params: any) => {
        setRowCount(params.api.getServerSideRowCount?.() ?? params.api.getModel().getRowCount());
        setLoading(false);
    }, []);

    const onModelUpdated = useCallback((params: any) => {
        setRowCount(params.api.getServerSideRowCount?.() ?? params.api.getModel().getRowCount());
        setLoading(false);
    }, []);

    // ─── Search / reset ───────────────────────────────────────────────────────
    const handleSearch = useCallback(
        (values: IObtSurveySearchCriteriaInput) => {
            const newFilters: IObtSurveySearchCriteriaInput = { ...values, pageNo: 0, pageSize: CACHE_BLOCK_SIZE };
            setLoading(true);
            setSearchFilters(newFilters);
            gridApi.current?.setServerSideDatasource(
                new ObtPartnerListDatasource(client, newFilters, CACHE_BLOCK_SIZE, trackLastSearch)
            );
        },
        [trackLastSearch]
    );

    const handleReset = useCallback(() => {
        const defaultFilters: IObtSurveySearchCriteriaInput = { pageNo: 0, pageSize: CACHE_BLOCK_SIZE };
        setSearchFilters(defaultFilters);
        gridApi.current?.setServerSideDatasource(
            new ObtPartnerListDatasource(client, defaultFilters, CACHE_BLOCK_SIZE, trackLastSearch)
        );
    }, [trackLastSearch]);

    // ─── Search by file (from ObtSearchForm modal) ────────────────────────────
    const handleSearchByFile = useCallback(
        (rows: FileSearchRow[]) => {
            const partnerNos = Array.from(new Set(rows.map(r => r.partnerno).filter(Boolean))).join(',');
            const systemCodes = Array.from(new Set(rows.map(r => r.sysid).filter(Boolean))).join(',');
            const companyCodes = Array.from(new Set(rows.map(r => r.companycode).filter(Boolean))).join(',');
            const categories = Array.from(new Set(rows.map(r => r.category).filter(Boolean))).join(',');

            const newFilters: IObtSurveySearchCriteriaInput = {
                partnerno: partnerNos || undefined,
                systemCode: systemCodes || undefined,
                companycode: companyCodes || undefined,
                partnertype: categories || undefined,
                pageNo: 0,
                pageSize: CACHE_BLOCK_SIZE,
            };
            setLoading(true);
            setSearchFilters(newFilters);
            gridApi.current?.setServerSideDatasource(
                new ObtPartnerListDatasource(client, newFilters, CACHE_BLOCK_SIZE, trackLastSearch)
            );
        },
        [trackLastSearch]
    );

    // ─── Row double-click → open partner details ──────────────────────────────
    const handleRowDoubleClick = useCallback(async (event: any) => {
        const row = event.data;
        if (!row) return;

        const identity: PartnerIdentity = {
            partnerno: row.partnerNo ?? '',
            companycode: row.companyCode ?? '',
            category: row.category ?? '',
            systemCode: row.systemCode ?? '',
            partnerName: row.partnerName ?? '',
            idpartner: Number.parseInt(String(row.partnerId), 10) || 0,
        };

        setLoading(true);
        try {
            const response = await client.query({
                query: QUERY_PARTNER_DETAILS,
                variables: {
                    filters: {
                        partnerno: identity.partnerno,
                        companycode: [identity.companycode],
                        category: [identity.category],
                        sysid: [identity.systemCode],
                        idpartner: identity.idpartner || undefined,
                        limit: 1,
                        offset: 0,
                        pageNo: 0,
                        pageSize: 1,
                    },
                },
                fetchPolicy: 'network-only',
            });

            const details: ObtPartnerDetailResponse | null = response?.data?.obtGetPartnerDetails ?? null;
            if (!details) {
                notification.warning({
                    message: intl.formatMessage({ id: 'obt.details.notFound', defaultMessage: 'Partner details not found.' }),
                });
                return;
            }

            setPartnerIdentity(identity);
            setPartnerDetails(details);
        } catch (err) {
            console.error('Failed to load partner details:', err);
            notification.error({
                message: intl.formatMessage({ id: 'obt.details.loadError', defaultMessage: 'Failed to load partner details.' }),
            });
        } finally {
            setLoading(false);
        }
    }, [intl]);

    const handleBackToSearch = useCallback(() => {
        setPartnerIdentity(null);
        setPartnerDetails(null);
        setActiveTab('search');
    }, []);

    // ─── View / Reset (matching DocumentsOverview pattern) ────────────────────
    const onSave = useCallback(() => {
        gridView.current.savePersonalizedView();
        forceMenuRefresh();
        notification.info({ message: intl.formatMessage({ id: 'report-result-preferences-saved', defaultMessage: 'View preferences saved.' }) });
    }, [intl]);

    const onApplyPerson = useCallback(() => {
        gridView.current.applyPersonalizedView();
    }, []);

    const onResetView = useCallback(() => {
        gridApi.current?.setFilterModel(null);
        gridView.current.reset();
    }, []);

    const viewMenu = (
        <Menu>
            <Menu.Item key="personal" disabled={!gridView.current.hasPersonalizedView()}>
                <UserOutlined onClick={onApplyPerson} />
                <a data-testid="obt-view-personal" onClick={onApplyPerson}>
                    {intl.formatMessage({ id: 'info-portal-personal', defaultMessage: 'Personal' })}
                </a>
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item key="save">
                <SaveOutlined onClick={onSave} />
                <a data-testid="obt-view-save" onClick={onSave}>
                    {intl.formatMessage({ id: 'info-portal-save', defaultMessage: 'Save' })}
                </a>
            </Menu.Item>
        </Menu>
    );

    // ─── Export ───────────────────────────────────────────────────────────────
    const getAllDataForExport = async (): Promise<any[]> => {
        const criteria = lastSearchRef.current;
        if (!criteria) return [];
        // Strip pagination — the download endpoint returns all matching rows
        const { pageNo: _p, pageSize: _s, sorters: _sort, ...criteriaWithoutPaging } = criteria;
        try {
            const response = await client.query({
                query: Obt_DownloadSurveyPartnerDataDocument,
                variables: { criteria: criteriaWithoutPaging },
                fetchPolicy: 'network-only',
            });
            return response?.data?.obtDownloadSurveyPartnerData?.surveyList ?? [];
        } catch (error) {
            console.error('Error fetching OBT data for export:', error);
            return [];
        }
    };

    const onExport = useCallback(async () => {
        if (!lastSearchRef.current) {
            Modal.warning({
                title: intl.formatMessage({ id: 'Warning-message', defaultMessage: 'Export' }),
                content: intl.formatMessage({
                    id: 'obt.export.pleaseSearchFirst',
                    defaultMessage: 'Please run a search before exporting.',
                }),
            });
            return;
        }
        try {
            setLoading(true);
            const allData = await getAllDataForExport();
            if (allData.length === 0) {
                Modal.warning({
                    title: intl.formatMessage({ id: 'Warning-message', defaultMessage: 'Export' }),
                    content: intl.formatMessage({ id: 'no-data-to-export', defaultMessage: 'No data available to export.' }),
                });
            } else {
                const columns: ColDef[] = columnDefs.current;
                await exportMonitoringData(
                    allData,
                    columns,
                    `${dayjs().format('YYYY-MM-DD_HH-mm')}-OBT-SurveyExport.xlsx`
                );
                notification.success({
                    message: intl.formatMessage({ id: 'export-success', defaultMessage: 'Export successful' }),
                    description: intl.formatMessage(
                        { id: 'obt.export.successDesc', defaultMessage: 'Exported {count} records successfully.' },
                        { count: allData.length }
                    ),
                });
            }
        } catch (error) {
            notification.error({
                message: intl.formatMessage({ id: 'export-error', defaultMessage: 'Export failed' }),
                description: intl.formatMessage({
                    id: 'obt.export.errorDesc',
                    defaultMessage: 'Failed to export data. Please try again.',
                }),
            });
        } finally {
            setLoading(false);
        }
    }, [intl]);

    // ─── Download (server-generated Excel) ────────────────────────────────────
    const onDownload = useCallback(async () => {
        if (!lastSearchRef.current) {
            Modal.warning({
                title: intl.formatMessage({ id: 'Warning-message', defaultMessage: 'Download' }),
                content: intl.formatMessage({
                    id: 'obt.download.pleaseSearchFirst',
                    defaultMessage: 'Please run a search before downloading.',
                }),
            });
            return;
        }

        if (rowCount > ALL_RESULTS_PAGE_SIZE) {
            Modal.warning({
                title: intl.formatMessage({ id: 'Warning-message', defaultMessage: 'Download' }),
                content: intl.formatMessage(
                    {
                        id: 'obt.download.refineQuery.limitExceeded',
                        defaultMessage:
                            'Your search returned {rowCount} records, which exceeds the download limit of {limit}. Please refine your query and try again.',
                    },
                    {
                        rowCount: rowCount.toLocaleString(),
                        limit: ALL_RESULTS_PAGE_SIZE.toLocaleString(),
                    }
                ),
            });
            return;
        }

        try {
            setLoading(true);
            const criteria = lastSearchRef.current;
            const { pageNo: _p, pageSize: _s, sorters: _sort, ...criteriaWithoutPaging } = criteria;
            const response = await client.query({
                query: PARTNER_DOWNLOAD_SURVEY_EXCEL,
                variables: { criteria: criteriaWithoutPaging },
                fetchPolicy: 'network-only',
            });
            const base64Data = response?.data?.obtDownloadSurveyExcel;
            if (!base64Data) {
                Modal.warning({
                    title: intl.formatMessage({ id: 'Warning-message', defaultMessage: 'Download' }),
                    content: intl.formatMessage({ id: 'no-data-to-export', defaultMessage: 'No data available to download.' }),
                });
                return;
            }
            const byteCharacters = atob(base64Data);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteArray[i] = byteCharacters.charCodeAt(i);
            }
            const blob = new Blob([byteArray], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            saveAs(blob, `${dayjs().format('YYYY-MM-DD_HH-mm')}-OBT-PartnerList.xlsx`);
            notification.success({
                message: intl.formatMessage({ id: 'download-success', defaultMessage: 'Download successful' }),
            });
        } catch (error) {
            notification.error({
                message: intl.formatMessage({ id: 'download-error', defaultMessage: 'Download failed' }),
                description: intl.formatMessage({
                    id: 'obt.download.errorDesc',
                    defaultMessage: 'Failed to download file. Please try again.',
                }),
            });
        } finally {
            setLoading(false);
        }
    }, [intl, rowCount]);

    // ─── Render ───────────────────────────────────────────────────────────────
    const showDetails = !!(partnerIdentity && partnerDetails);
    const tabs = [
        {
            key: 'search',
            label: intl.formatMessage({ id: 'obt.tab.search', defaultMessage: 'Search' }),
            children: (
                <>
                    {/* Search filters (collapsible) */}
                    <div className={styles.filterSectionWrap}>
                        <Collapse defaultActiveKey={['obt-search-filters']}>
                            <Collapse.Panel
                                key="obt-search-filters"
                                header={intl.formatMessage({ id: 'search-filters', defaultMessage: 'Search Filters' })}
                            >
                                <ObtSearchForm
                                    form={form}
                                    currentUser={props.currentUser}
                                    countryOptions={countryOptions}
                                    companyCodeOptions={companyCodeOptions}
                                    systemOptions={systemOptions}
                                    optionsLoading={optionsLoading}
                                    onSearch={handleSearch}
                                    onReset={handleReset}
                                    onSearchByFile={handleSearchByFile}
                                />
                            </Collapse.Panel>
                        </Collapse>
                    </div>

                    {/* Grid area */}
                    <div className={styles.gridContainer}>
                        <div className={styles.gridHeader}>
                            <div>
                                <label>
                                    {intl.formatMessage({ id: 'welcome-No_documents', defaultMessage: 'Number of Records' })}
                                </label>
                                <Space>
                                    <Badge
                                        count={rowCount}
                                        style={{ backgroundColor: '#52c41a' }}
                                        overflowCount={99999999}
                                        title="Number of Partners"
                                    />
                                </Space>
                            </div>
                            <Row gutter={24} style={{ paddingTop: '5px' }}>
                                <Col style={{ paddingRight: '5px' }}>
                                    <Dropdown.Button
                                        data-testid="obt-view-customization"
                                        overlay={viewMenu}
                                        trigger={['click']}
                                        icon={<DownOutlined />}
                                        size="middle"
                                    >
                                        <EyeOutlined />
                                        {intl.formatMessage({ id: 'info-portal-view', defaultMessage: 'View' })}
                                    </Dropdown.Button>
                                </Col>
                                <Col style={{ paddingRight: '5px' }}>
                                    <Tooltip title={intl.formatMessage({ id: 'reset-filter', defaultMessage: 'Reset' })}>
                                        <Button
                                            data-testid="obt-reset-view-button"
                                            onClick={onResetView}
                                            size="middle"
                                            icon={<ReloadOutlined />}
                                        >
                                            {intl.formatMessage({ id: 'info-portal-reset', defaultMessage: 'Reset' })}
                                        </Button>
                                    </Tooltip>
                                </Col>
                                <Col style={{ paddingRight: '5px' }}>
                                    <Tooltip title={intl.formatMessage({ id: 'obt.toolbar.export', defaultMessage: 'Export' })}>
                                        <Button
                                            data-testid="obt-export-button"
                                            icon={<ExportOutlined />}
                                            size="middle"
                                            onClick={onExport}
                                        >
                                            {intl.formatMessage({ id: 'obt.toolbar.export', defaultMessage: 'Export' })}
                                        </Button>
                                    </Tooltip>
                                </Col>
                                <Col style={{ paddingRight: '5px' }}>
                                    <Tooltip title={intl.formatMessage({ id: 'welcome-download-button', defaultMessage: 'Download' })}>
                                        <Button
                                            data-testid="obt-download-button"
                                            icon={<DownloadOutlined />}
                                            size="middle"
                                            onClick={onDownload}
                                        >
                                            {intl.formatMessage({ id: 'welcome-download-button', defaultMessage: 'Download' })}
                                        </Button>
                                    </Tooltip>
                                </Col>
                            </Row>
                        </div>
                        <div className={`ag-theme-balham ${styles.gridBody}`}>
                            <AgGridReact
                                key="obtPartnerListGrid"
                                rowModelType="serverSide"
                                columnDefs={gridView.current.getView()}
                                defaultColDef={DEFAULT_COL_DEF}
                                cacheBlockSize={CACHE_BLOCK_SIZE}
                                rowSelection="multiple"
                                pagination={true}
                                paginationAutoPageSize={true}
                                animateRows={true}
                                sideBar={true}
                                enableRangeSelection={true}
                                onGridReady={onGridReady}
                                onFirstDataRendered={onFirstDataRendered}
                                onModelUpdated={onModelUpdated}
                                onRowDoubleClicked={handleRowDoubleClick}
                            />
                        </div>
                    </div>
                </>
            ),
        },
        ...(obtAccessRights.canMaintainBulkUpdate
            ? [
                  {
                      key: 'update',
                      label: intl.formatMessage({ id: 'obt.tab.update', defaultMessage: 'Bulk Partner Contacts Update' }),
                      children: <ObtPartnerContactsBulkUpdateTab />,
                  },
              ]
            : []),
    ];

    if (!obtAccessRights.canAccessObt) {
        return <div className={styles.obtPage} />;
    }

    return (
        <Spin spinning={loading} size="large" indicator={<LoadingOutlined style={{ fontSize: 50 }} />}>
            <div className={styles.obtPage}>
                {/* Partner Details — shown on top when active */}
                {showDetails && (
                    <ObtPartnerDetailsTab
                        partnerIdentity={partnerIdentity}
                        partnerDetails={partnerDetails}
                        currentUser={props.currentUser}
                        distinctEntitlements={props.distinctEntitlements}
                        obtAccessRights={obtAccessRights}
                        onBackToSearch={handleBackToSearch}
                    />
                )}

                {/* Search/Update tabs — hidden (not unmounted) when details are shown */}
                <div style={{ display: showDetails ? 'none' : undefined }}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        destroyInactiveTabPane={false}
                        items={tabs}
                    />
                </div>
            </div>
        </Spin>
    );
};

export default ObtLandingPage;
